import {
  IconButton,
  MenuItem,
  MenuSeparator,
  toast,
  useConfirmModal,
} from '@affine/component';
import { useDocHelper } from '@affine/core/components/blocksuite/block-suite-page-list/utils';
import { useBlockSuiteMetaHelper } from '@affine/core/components/hooks/affine/use-block-suite-meta-helper';
import { useSharingUrl } from '@affine/core/components/hooks/affine/use-share-url';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { useWikiActions } from '@affine/core/components/page-list/operation-menu-items/wiki-action/use-wiki-action';
import { IsFavoriteIcon } from '@affine/core/components/pure/icons';
import { WorkspacePermissionService } from '@affine/core/modules/permissions';
import { CompatibleFavoriteItemsAdapter } from '@affine/core/modules/properties';
import { useGetDocsWiki } from '@affine/core/modules/wiki';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import {
  LinkIcon as LinkIconRC,
  SplitViewIcon,
} from '@docnboard/icons/blocksuite/rc';
import {
  AddSmallIcon,
  CopyIcon,
  DocumentAddIcon,
  InformationStrokeIcon,
  LinkIcon,
  TrashIcon,
} from '@docnboard/icons/rc';
import {
  DocsService,
  FeatureFlagService,
  useLiveData,
  useServices,
  WorkspaceService,
} from '@toeverything/infra';
import { useCallback, useMemo } from 'react';

import type { NodeOperation } from '../../tree/types';

export const useExplorerDocNodeOperations = (
  docId: string,
  options: {
    openInfoModal: () => void;
    openNodeCollapsed: () => void;
  }
): NodeOperation[] => {
  const t = useI18n();
  const {
    workspaceService,
    workbenchService,
    docsService,
    compatibleFavoriteItemsAdapter,
    featureFlagService,
    workspacePermissionService,
  } = useServices({
    WorkspaceService,
    DocsService,
    WorkbenchService,
    CompatibleFavoriteItemsAdapter,
    FeatureFlagService,
    WorkspacePermissionService,
  });
  const enableMultiView = useLiveData(
    featureFlagService.flags.enable_multi_view.$
  );
  const { openConfirmModal } = useConfirmModal();

  const docRecord = useLiveData(docsService.list.doc$(docId));

  const isReadOnly = useLiveData(
    workspacePermissionService.permission.isReadOnly$
  );

  const { createDoc } = useDocHelper();

  const { onClickCopyLink } = useSharingUrl({
    workspaceId: workspaceService.workspace.id,
    pageId: docId,
  });
  // TODO подумать над оптимизацией (мб добавить в запрос фильтр по папкам)
  // получать весь список - не вариант, делать запрос для каждого документа - не вариант
  const { getWikiDocById } = useGetDocsWiki(workspaceService.workspace.id, {
    enabled: !isReadOnly,
  });
  const wikiDoc = useMemo(() => getWikiDocById(docId), [docId, getWikiDocById]);

  const favorite = useLiveData(
    useMemo(() => {
      return compatibleFavoriteItemsAdapter.isFavorite$(docId, 'doc');
    }, [docId, compatibleFavoriteItemsAdapter])
  );

  const { duplicate } = useBlockSuiteMetaHelper();
  const handleDuplicate = useCallback(() => {
    duplicate(docId, true);
    track.$.navigationPanel.docs.createDoc();
  }, [docId, duplicate]);
  const handleOpenInfoModal = useCallback(() => {
    track.$.docInfoPanel.$.open();
    options.openInfoModal();
  }, [options]);

  const handleMoveToTrash = useCallback(() => {
    if (!docRecord) {
      return;
    }
    openConfirmModal({
      title: t['com.affine.moveToTrash.title'](),
      description: t['com.affine.moveToTrash.confirmModal.description']({
        title: docRecord.title$.value,
      }),
      confirmText: t['com.affine.moveToTrash.confirmModal.confirm'](),
      cancelText: t['com.affine.moveToTrash.confirmModal.cancel'](),
      confirmButtonOptions: {
        variant: 'error',
      },
      onConfirm() {
        docRecord.moveToTrash();
        track.$.navigationPanel.docs.deleteDoc({
          control: 'button',
        });
        toast(t['com.affine.toastMessage.movedTrash']());
      },
    });
  }, [docRecord, openConfirmModal, t]);

  const onCopyFullLink = useCallback(() => {
    onClickCopyLink('full');
  }, [onClickCopyLink]);

  const handleOpenInNewTab = useCallback(() => {
    workbenchService.workbench.openDoc(docId, {
      at: 'new-tab',
    });
    track.$.navigationPanel.organize.openInNewTab({
      type: 'doc',
    });
  }, [docId, workbenchService]);

  const handleOpenInSplitView = useCallback(() => {
    workbenchService.workbench.openDoc(docId, {
      at: 'beside',
    });
    track.$.navigationPanel.organize.openInSplitView({
      type: 'doc',
    });
  }, [docId, workbenchService.workbench]);

  const handleAddLinkedPage = useAsyncCallback(async () => {
    const newDoc = createDoc();
    if (!newDoc) {
      return;
    }

    // TODO: handle timeout & error
    await docsService.addLinkedDoc(docId, newDoc.id);
    track.$.navigationPanel.docs.createDoc({ control: 'linkDoc' });
    track.$.navigationPanel.docs.linkDoc({ control: 'createDoc' });
    workbenchService.workbench.openDoc(newDoc.id);
    options.openNodeCollapsed();
  }, [createDoc, docsService, docId, workbenchService.workbench, options]);

  const handleToggleFavoriteDoc = useCallback(() => {
    compatibleFavoriteItemsAdapter.toggle(docId, 'doc');
    track.$.navigationPanel.organize.toggleFavorite({
      type: 'doc',
    });
  }, [docId, compatibleFavoriteItemsAdapter]);

  // TODO проверку на роль?
  // DNB_FIX добавить кнопки управления wiki
  const { activeWikiActions } = useWikiActions(docId, wikiDoc);

  const wikiMenuActions = useMemo(() => {
    return activeWikiActions.map((action, idx) => ({
      index: 300 + idx,
      view: action,
    }));
  }, [activeWikiActions]);

  return useMemo(
    () => [
      {
        index: 0,
        inline: true,
        view: (
          <IconButton
            size="16"
            icon={<AddSmallIcon />}
            tooltip={t['com.affine.rootAppSidebar.explorer.doc-add-tooltip']()}
            onClick={handleAddLinkedPage}
          />
        ),
      },
      {
        index: 50,
        view: (
          <MenuItem
            prefixIcon={<InformationStrokeIcon />}
            onClick={handleOpenInfoModal}
          >
            {t['com.affine.doc-properties.page-info.view']()}
          </MenuItem>
        ),
      },
      {
        index: 99,
        view: (
          <MenuItem
            prefixIcon={<DocumentAddIcon />}
            onClick={handleAddLinkedPage}
          >
            {t['com.affine.page-operation.add-linked-page']()}
          </MenuItem>
        ),
      },
      {
        index: 99,
        view: (
          <MenuItem prefixIcon={<CopyIcon />} onClick={handleDuplicate}>
            {t['com.affine.header.option.duplicate']()}
          </MenuItem>
        ),
      },
      {
        index: 99,
        view: (
          <MenuItem prefixIcon={<LinkIcon />} onClick={handleOpenInNewTab}>
            {t['com.affine.workbench.tab.page-menu-open']()}
          </MenuItem>
        ),
      },
      {
        index: 99,
        view: (
          <MenuItem prefixIcon={<LinkIconRC />} onClick={onCopyFullLink}>
            {t['com.affine.share-menu.copy']()}
          </MenuItem>
        ),
      },
      ...(BUILD_CONFIG.isElectron && enableMultiView
        ? [
            {
              index: 100,
              view: (
                <MenuItem
                  prefixIcon={<SplitViewIcon />}
                  onClick={handleOpenInSplitView}
                >
                  {t['com.affine.workbench.split-view.page-menu-open']()}
                </MenuItem>
              ),
            },
          ]
        : []),
      {
        index: 199,
        view: (
          <MenuItem
            prefixIcon={<IsFavoriteIcon favorite={favorite} />}
            onClick={handleToggleFavoriteDoc}
          >
            {favorite
              ? t['com.affine.favoritePageOperation.remove']()
              : t['com.affine.favoritePageOperation.add']()}
          </MenuItem>
        ),
      },
      ...wikiMenuActions,
      {
        index: 9999,
        view: <MenuSeparator key="menu-separator" />,
      },
      {
        index: 10000,
        view: (
          <MenuItem
            type={'danger'}
            prefixIcon={<TrashIcon />}
            onClick={handleMoveToTrash}
          >
            {t['com.affine.moveToTrash.title']()}
          </MenuItem>
        ),
      },
    ],
    [
      enableMultiView,
      favorite,
      handleAddLinkedPage,
      handleDuplicate,
      handleMoveToTrash,
      handleOpenInNewTab,
      handleOpenInSplitView,
      handleOpenInfoModal,
      handleToggleFavoriteDoc,
      onCopyFullLink,
      t,
      wikiMenuActions,
    ]
  );
};
